// Показ числа. Считать здесь нечего — только оформить то, что пришло.
//
// ⚠ `Intl.NumberFormat.format` принимает **строку** и разбирает её как точную
// десятичную величину (ECMA-402, Intl.NumberFormat v3). Поэтому строка с
// сервера доходит до экрана, ни разу не побывав `number`: перевод в число с
// плавающей точкой — та самая потеря копейки, ради которой на сервере заведён
// `Decimal` (`server/fortify/core/result.py`).

import { assertMoneyReadable, type Money } from "./types";

/** Гривня словами украинской локали: «256 431,07 ₴». */
const MONEY = new Intl.NumberFormat("uk-UA", {
  style: "currency",
  currency: "UAH",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/** То же число без знака валюты — для колонки, где валюта названа шапкой. */
const PLAIN = new Intl.NumberFormat("uk-UA", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/** Счётчик штук: «7 з 12», «і ще 4». Не деньги. */
const COUNT = new Intl.NumberFormat("uk-UA", { maximumFractionDigits: 0 });

/**
 * Строка с сервера — в тот вид, который ждёт `Intl`.
 *
 * ⚠ Приведение типа здесь законно ровно потому, что перед ним стоит
 * `assertMoneyReadable`: он уже проверил, что в строке десятичное число, и
 * ничего иного до этой строки не доходит. `StringNumericLiteral` —
 * шаблонный тип `` `${number}` ``, и обычная `string` под него не подходит,
 * хотя во время работы `Intl` принимает именно строку и разбирает её точно.
 *
 * ⚠ Альтернатива — `Number(amount)` — здесь запрещена: это перевод денег в
 * число с плавающей точкой, то есть потерянная копейка.
 */
function exact(value: Money, where: string): Intl.StringNumericLiteral {
  assertMoneyReadable(value, where);
  return value.amount as Intl.StringNumericLiteral;
}

export function formatMoney(value: Money, where: string): string {
  return MONEY.format(exact(value, where));
}

export function formatAmount(value: Money, where: string): string {
  return PLAIN.format(exact(value, where));
}

export function formatCount(count: number): string {
  return COUNT.format(count);
}

/** Время последней удачной синхронизации: «14:02, 6 годин тому» приходит с
 *  сервера готовой строкой; здесь только час дня из отметки времени. */
//
// ⚠⚠ **Пояс назван, а не взят у браузера.** Вся система живёт киевским днём —
// на сервере он держится общим ядром `fortify/kyiv.py`, и отсчёты, горизонты и
// сроки считаются по нему. Пока пояс брался у браузера, один и тот же момент
// читался по-разному на разных машинах: ноутбук, забытый в чужом поясе, показал
// бы срок сессии и день платежа на несколько часов мимо — и никто бы не
// заметил, потому что числа выглядели бы обычно. Живая проба таска 11 это и
// поймала: браузер стенда показал срок сессии на три часа раньше киевского.
const KYIV = "Europe/Kyiv";

const TIME = new Intl.DateTimeFormat("uk-UA", {
  hour: "2-digit",
  minute: "2-digit",
  timeZone: KYIV,
});
const DAY = new Intl.DateTimeFormat("uk-UA", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  timeZone: KYIV,
});

export function formatTime(when: Date): string {
  return TIME.format(when);
}

export function formatDay(when: Date): string {
  return DAY.format(when);
}

const DAY_SHORT = new Intl.DateTimeFormat("uk-UA", { day: "2-digit", month: "2-digit", timeZone: KYIV });

/** «01.10» — день без года, когда год и так понятен (нынешний); другой год — полный день. */
export function formatDayNear(when: Date, now: Date = new Date()): string {
  return DAY.format(when).slice(-4) === DAY.format(now).slice(-4) ? DAY_SHORT.format(when) : DAY.format(when);
}

/** «вересень 2026» — месяц словом из дня (ISO) в нём. */
export function formatMonth(iso: string): string {
  return new Intl.DateTimeFormat("uk-UA", { month: "long", year: "numeric", timeZone: "UTC" })
    .format(new Date(`${iso}T12:00:00Z`))
    .replace(/\s?р\.$/, "");
}

/**
 * Порядок двух сумм сервера («1991574.67», «-12.50») — для сортировки колонки.
 *
 * ⚠ Сравниваются строки, а не числа: знак, длина целой части, затем цифры по
 * разрядам. В число с плавающей точкой сумма не переводится (та же потеря
 * копейки, что и в показе). Ничего не складывается — это порядок, не величина.
 */
export function compareAmounts(a: string, b: string): number {
  const [signA, intA, fracA] = parts(a);
  const [signB, intB, fracB] = parts(b);
  if (signA !== signB) return signA < signB ? -1 : 1;
  const width = Math.max(fracA.length, fracB.length);
  const digitsA = intA.padStart(Math.max(intA.length, intB.length), "0") + fracA.padEnd(width, "0");
  const digitsB = intB.padStart(Math.max(intA.length, intB.length), "0") + fracB.padEnd(width, "0");
  const magnitude = digitsA === digitsB ? 0 : digitsA < digitsB ? -1 : 1;
  return signA < 0 ? -magnitude : magnitude;
}

/** Знак (−1, 0, 1), целая часть без ведущих нулей, дробная без хвостовых. */
function parts(text: string): [number, string, string] {
  const negative = text.startsWith("-");
  const [whole = "0", fraction = ""] = text.replace(/^[-+]/, "").split(".");
  const int = whole.replace(/^0+/, "");
  const frac = fraction.replace(/0+$/, "");
  if (int === "" && frac === "") return [0, "", ""];
  return [negative ? -1 : 1, int, frac];
}
